/**
 * Service-specific Route Logic
 * Handles A2/R2 service availability and route switching
 */

import { TENANT_ROUTES } from './routes';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from './serviceType';

/**
 * Service-specific routes - only routes that deviate from dual-service default
 * By default, all routes in TENANT_ROUTES are available in both A2 and R2
 * Use these lists only for exceptions
 */
export const SERVICE_ROUTE_EXCEPTIONS = {
  // Routes that only exist in A2 service
  A2_ONLY: [] as const,
  // Routes that only exist in R2 service
  R2_ONLY: [] as const,
} as const;

/**
 * Routes that should NOT navigate away on service switch
 * Pages on these routes stay intact when service type changes
 */
export const SERVICE_SWITCH_EXCLUSIONS = {
  // Patient edit page with ID should stay on the same page
  PATIENTS_EDIT_WITH_ID: 'patients_edit_with_id' as const,
} as const;

/**
 * Checks if a route is available in a specific service type
 * @param routeSegment - The route segment (e.g., 'visit_clinic')
 * @param serviceType - The service type ('A2' or 'R2')
 * @returns true if the route is available in that service
 *
 * Logic:
 * - By default, all routes are dual-service (available in both)
 * - Exception routes are listed in SERVICE_ROUTE_EXCEPTIONS
 */
export const isRouteAvailableInService = (
  routeSegment: string,
  serviceType: 'A2' | 'R2'
): boolean => {
  const segment = routeSegment.toLowerCase().replace(/-/g, '_');
  const isSingleSegmentException = (route: string) => {
    const routeSegments = route.split('/').filter(Boolean);
    return routeSegments.length === 1 && routeSegments[0].toLowerCase().replace(/-/g, '_') === segment;
  };

  // Check if route is in the exception lists
  if (serviceType === SERVICE_TYPE_A2) {
    if ((SERVICE_ROUTE_EXCEPTIONS.R2_ONLY as readonly string[]).some(isSingleSegmentException)) {
      return false; // R2-only route, not available in A2
    }
  } else if (serviceType === SERVICE_TYPE_R2) {
    if ((SERVICE_ROUTE_EXCEPTIONS.A2_ONLY as readonly string[]).some(isSingleSegmentException)) {
      return false; // A2-only route, not available in R2
    }
  }

  return true; // Default: available in requested service
};

const normalizeRouteSegment = (segment: string): string => segment.toLowerCase().replace(/-/g, '_');

const isPathMatchedByException = (pathSegments: string[], exceptionPath: string): boolean => {
  const exceptionSegments = exceptionPath.split('/').filter(Boolean).map(normalizeRouteSegment);
  if (exceptionSegments.length === 0 || pathSegments.length < exceptionSegments.length) {
    return false;
  }

  return exceptionSegments.every((segment, index) => pathSegments[index] === segment);
};

/**
 * Checks if a tenant URL path is available in a service type.
 * @param currentPathname - Full browser pathname, e.g. '/medix/patients/continuation/add/...'
 * @param serviceType - The active service type ('A2' or 'R2')
 */
export const isTenantPathAvailableInService = (
  currentPathname: string,
  serviceType: 'A2' | 'R2'
): boolean => {
  const [pathWithoutHash] = currentPathname.split('#');
  const [pathWithoutQuery] = pathWithoutHash.split('?');
  const segments = pathWithoutQuery.split('/').filter(Boolean);

  // Tenant routes are mounted as /:tenant/*, so drop the tenant prefix.
  const routeSegments = segments.slice(1).map(normalizeRouteSegment);
  if (routeSegments.length === 0) {
    return true;
  }

  const unavailableRoutes =
    serviceType === SERVICE_TYPE_A2
      ? SERVICE_ROUTE_EXCEPTIONS.R2_ONLY
      : SERVICE_ROUTE_EXCEPTIONS.A2_ONLY;

  return !(unavailableRoutes as readonly string[]).some(route =>
    isPathMatchedByException(routeSegments, route)
  );
};

/**
 * Determines the new path when switching services
 * @param currentPathname - The current full pathname (e.g., '/clinic/visit_clinic')
 * @param currentServiceType - The current service type ('A2' or 'R2')
 * @returns The new pathname to navigate to
 *
 * Logic:
 * - If current route exists in both services: stay on same path
 * - If current route only exists in current service: navigate to home of new service
 *
 * Example:
 * - User on /clinic/document_templates in A2 → switch to R2 → /clinic/document_templates (dual route)
 * - User on /clinic/visit_clinic in A2 → switch to R2 → /clinic (visit_clinic is A2-only)
 */
export const getPathAfterServiceSwitch = (
  currentPathname: string,
  currentServiceType: 'A2' | 'R2'
): string => {
  const segments = currentPathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return TENANT_ROUTES.ROOT('default');
  }

  const tenant = segments[0];
  const newServiceType = currentServiceType === SERVICE_TYPE_A2 ? SERVICE_TYPE_R2 : SERVICE_TYPE_A2;

  if (!isTenantPathAvailableInService(currentPathname, newServiceType)) {
    return TENANT_ROUTES.ROOT(tenant);
  }

  // The path can also be invalid for the service we are leaving — e.g. an A2-only path somehow
  // reached while in R2. There is nothing sensible to map from, and the per-module fallbacks below
  // would aim at a module entry page that may not exist (`patients/init`), so go home instead.
  // Note isRouteAvailableInService() below only matches single-segment exceptions, which is why
  // this uses the path-aware check.
  if (!isTenantPathAvailableInService(currentPathname, currentServiceType)) {
    return TENANT_ROUTES.ROOT(tenant);
  }

  // Patient-scoped modules whose URL shape is `/<tenant>/<module>/<patientId>/<list|create|edit|...>`.
  // On service switch: list stays; create/edit/continue redirect to the list for that patient.
  const PATIENT_SCOPED_MODULES = [
    'consents',
    'treatment-reports',
    'long_term_frequents',
    'rezept_application_counts',
    'checkin_mails',
    'patient_addresses',
  ];
  for (const moduleSegment of PATIENT_SCOPED_MODULES) {
    const idx = segments.indexOf(moduleSegment);
    if (idx === -1) continue;
    const tenantPrefix = segments.slice(0, idx).join('/');
    const patientId = segments[idx + 1];
    const subRoute = segments[idx + 2];
    // Only apply patient-scoped logic when the next segment is a numeric patient id.
    // Non-numeric sub-routes (e.g. consents/treatment_period) are module-level pages
    // that stay as-is across service switches.
    if (!patientId || !/^\d+$/.test(patientId)) {
      return currentPathname;
    }
    if (subRoute === 'list' && patientId) {
      return currentPathname;
    }
    return `/${tenantPrefix}/${moduleSegment}/${patientId}/list`;
  }

  const routeSegment = segments[1]?.toLowerCase().replace(/-/g, '_');

  if (!routeSegment) {
    return TENANT_ROUTES.ROOT(tenant);
  }

  // Check if route exists in current service
  if (!isRouteAvailableInService(routeSegment, currentServiceType)) {
    // Current path is invalid for current service - go home
    return TENANT_ROUTES.ROOT(tenant);
  }

  // Check if route exists in new service
  if (isRouteAvailableInService(routeSegment, newServiceType)) {
    // Route exists in both services.
    // If the path contains a numeric ID (detail page), go to the list/entry page
    // instead — the specific record may not exist in the other service.
    const hasNumericId = segments.slice(2).some(s => /^\d+$/.test(s));
    if (hasNumericId) {
      // Special exclusion: Patients edit, copy, or histories page with ID stays on the same path.
      // `/patients/<id>/...` (dminfo/edit, histories) stays too: the patient record is
      // shared across services (PatientController never reads X-Service-Type), and there is no
      // `patients/init` route for the fallback below to land on — it rendered NotFound.
      if (
        routeSegment === 'patients' &&
        (segments[2]?.toLowerCase() === 'edit' ||
          segments[2]?.toLowerCase() === 'copy' ||
          /^\d+$/.test(segments[2] ?? '') ||
          segments.some(s => s.toLowerCase() === 'histories'))
      ) {
        return currentPathname;
      }
      // patients/continuation/add|edit/... — the add page writes a visit for the service it
      // runs in, so a switch must not replay it there; go back to the 継続受診者登録 list.
      if (routeSegment === 'patients' && segments[2]?.toLowerCase() === 'continuation') {
        return `/${tenant}/patients/continuation`;
      }
      // visit_clinic edit page is patient-scoped and supports A2/R2 via X-Service-Type.
      if (routeSegment === 'visit_clinic' && segments[2]?.toLowerCase() === 'edit') {
        return currentPathname;
      }
      // visits/schedule/<massagerId>/<clinicLocationId>/<date> — massager + location are
      // service-specific (A2/R2 picker rows are filtered by service_type), so on service
      // switch we drop the params and let the page pick the first available row for the
      // new service.
      if (routeSegment === 'visits' && segments[2]?.toLowerCase() === 'schedule') {
        return TENANT_ROUTES.VISITS.SCHEDULE(tenant);
      }
      // visits/distance_change/<visitdate>/<massagerId>/<clinicLocationId> — 距離編集 is launched
      // from 訪問予定 and its massager/location params are service-specific, so go back there.
      // There is no `visits/init` route, so the generic fallback below rendered NotFound.
      if (routeSegment === 'visits' && segments[2]?.toLowerCase() === 'distance_change') {
        return TENANT_ROUTES.VISITS.SCHEDULE(tenant);
      }
      return `/${tenant}/${segments[1]}/init`;
    }
    const pathAfterTenant = currentPathname.slice(1 + tenant.length);
    return `/${tenant}${pathAfterTenant}`;
  }

  // Route only exists in current service - go home of new service
  return TENANT_ROUTES.ROOT(tenant);
};
